/* oxlint-disable max-lines, import/max-dependencies -- lifecycle orchestrator, splitting would scatter tightly-coupled control flow; the WorkPreservation import pushes it one over the dependency cap */
import type {
  AgentRunnerStatus,
  AgentMode,
  RunnerMode,
  TaskContext,
  TaskContextDTO,
} from "@project/shared";
import { DEFAULT_SONNET_MODEL, PRE_BUILD_TASK_STATUSES, hasTaskPlan } from "@project/shared";
import {
  AgentConnection,
  type AgentConnectionConfig,
  type IncomingMessage,
} from "../connection/agent-connection.js";
import { isPermissionDeniedError } from "../connection/auth-errors.js";
import { ModeController, type ModeTaskContext } from "./mode-controller.js";
import { Lifecycle, DEFAULT_LIFECYCLE_CONFIG, type LifecycleConfig } from "./lifecycle.js";
import { QueryBridge } from "./query-bridge.js";
import type { AgentRunnerConfig } from "../runner-types.js";
import {
  ensureOnTaskBranch,
  flushAllPendingWork,
  flushPendingChanges,
  restoreWipSnapshot,
  updateRemoteToken,
} from "./git-utils.js";
import { mapChatHistory, readAgentVersion } from "./session-runner-helpers.js";
import { sampleKeyUsage } from "../execution/usage-sampler.js";
import { awaitGitReady } from "../setup/git-ready.js";
import {
  registerBootMilestoneSocketFallback,
  reportBootMilestone,
} from "../setup/boot-milestone.js";
import { PortDiscovery } from "./port-discovery.js";
import { CodespacePortVisibility } from "./codespace-port-visibility.js";
import { handlePullBranch } from "./parent-pull-handler.js";
import { isHeavyGateActive } from "./heavy-gate.js";
import {
  LoopLagMonitor,
  loopStatusForRunnerStatus,
  type LoopStatus,
} from "../connection/loop-lag.js";
import { BackgroundWorkTracker } from "./background-work.js";
import { ensureClaudeCredentials, removeConveyorCredentials } from "../harness/pty/credentials.js";

// ── Configuration ──────────────────────────────────────────────────────────

/** Modes whose initial instructions auto-run (submit) at session start. */
const AUTO_RUN_MODES = new Set<string>(["building", "auto", "review", "discovery", "chat"]);

/**
 * Runner modes that drive themselves with no human at the terminal and no
 * follow-up message queued behind the initial turn. When one of these stalls,
 * nothing external will un-park it — see `requeueWedgedInitialQuery`.
 */
const AUTONOMOUS_RUNNER_MODES = new Set<string>(["pack", "pm", "code-review"]);

export interface SessionRunnerConfig {
  connection: AgentConnectionConfig;
  agentMode?: AgentMode;
  runnerMode?: RunnerMode;
  isAuto?: boolean;
  workspaceDir: string;
  model?: string;
  lifecycle?: Partial<LifecycleConfig>;
}

export interface SessionRunnerCallbacks {
  onStatusChange: (status: AgentRunnerStatus) => void | Promise<void>;
  onEvent: (event: Record<string, unknown>) => void | Promise<void>;
}

export interface SessionRunnerPortDiscovery {
  start(): Promise<void>;
  stop(): void;
}

/** Duck-typed handle onto the boot supervisor — just enough surface for the
 *  runner to release the app's start command once the core loop is live.
 *  Avoids importing the concrete WorkspaceCommandSupervisor class here. */
export interface SessionRunnerWorkspaceCommands {
  notifyLoopReady(): void;
}

export interface SessionRunnerDependencies {
  portDiscovery?: SessionRunnerPortDiscovery;
}

// ── SessionRunner: main lifecycle orchestrator ─────────────────────────────

export class SessionRunner {
  readonly connection: AgentConnection;
  readonly mode: ModeController;
  readonly lifecycle: Lifecycle;

  private readonly config: SessionRunnerConfig;
  private readonly callbacks: SessionRunnerCallbacks;
  private _state: AgentRunnerStatus = "connecting";
  private stopped = false;
  private interrupted = false;
  /** Defense-in-depth: set when the agent emits a "completed" event.
   *  Prevents the core loop from processing any further messages. */
  private completedThisTurn = false;
  /** Absolute deadline (ms epoch) at which the dormant idle wait must time
   *  out. Set on the FIRST entry into dormant idle for a given completion
   *  cycle and preserved across iterations so inbound critical messages
   *  cannot extend the bound past the configured `dormantTimeoutMs`. Reset
   *  to null when the agent transitions out of dormant idle (a wake actually
   *  promotes it back to a working turn). */
  private dormantDeadline: number | null = null;

  private taskContext: ModeTaskContext | null = null;
  private fullContext: TaskContext | null = null;
  /** getTaskContext promise started in connect() so the longest server
   *  roundtrip overlaps the room join + port scan; run() awaits it. */
  private contextPrefetch: Promise<TaskContextDTO> | null = null;
  private queryBridge: QueryBridge | null = null;
  private inputResolver: ((msg: IncomingMessage | null) => void) | null = null;
  private pendingMessages: IncomingMessage[] = [];
  /** Guards overlapping runs of the periodic git flush. */
  private periodicFlushInFlight = false;
  /** Consecutive flush ticks skipped because a heavy gate was running. */
  private gateSkipCount = 0;
  /** Max consecutive gate-deferred ticks (~2 min each) before flushing anyway. */
  private static readonly MAX_GATE_SKIPS = 10;
  /** Runtime preview-port poller (v3 dynamic port discovery). */
  private readonly portDiscovery: SessionRunnerPortDiscovery;
  /** Main event-loop lag measurement, shared with the heartbeat worker. */
  private readonly loopLag = new LoopLagMonitor();
  /** Background work (a `run_in_background` gate, a backgrounded subagent) the
   *  turn launched that is still running in the pod after the turn ended. While
   *  it reports pending, heartbeats go out non-idle so the workspace activity
   *  clock keeps being bumped and the reconciler can't sleep the pod mid-gate. */
  private readonly backgroundWork = new BackgroundWorkTracker({
    onChange: () => this.refreshLoopStatus(),
  });
  /** Boot supervisor handle, set post-construction once it's known (it is
   *  started alongside connect(), before the runner itself exists in some
   *  call sites) — notified when the core loop goes live so it can release
   *  the app's start command. Optional: never set in tests/paths that don't
   *  wire a supervisor. */
  private workspaceCommands: SessionRunnerWorkspaceCommands | null = null;
  /** Guards the one-shot `agent_live` boot-milestone report — fired on the
   *  first harness event (createQueryBridge's onEvent hook). */
  private agentLiveReported = false;

  constructor(
    config: SessionRunnerConfig,
    callbacks: SessionRunnerCallbacks,
    deps: SessionRunnerDependencies = {},
  ) {
    this.config = config;
    this.callbacks = callbacks;

    // Compose components
    this.connection = new AgentConnection(config.connection);
    // On the codespace backend a runtime-discovered port is forwarded PRIVATE,
    // so its app.github.dev preview URL 404s for anyone else until visibility
    // is flipped. Best-effort, no-op off-codespace, never throws.
    const portVisibility = new CodespacePortVisibility();
    this.portDiscovery =
      deps.portDiscovery ??
      new PortDiscovery({
        report: (ports) => this.connection.reportDiscoveredPorts(ports),
        onReported: (ports) => portVisibility.ensureVisible(ports.map(({ port }) => port)),
      });

    const initialMode =
      config.agentMode ??
      (config.runnerMode === "pm" ? (config.isAuto ? "auto" : "discovery") : "building");
    this.mode = new ModeController(initialMode, config.runnerMode, config.isAuto);

    const lifecycleConfig = { ...DEFAULT_LIFECYCLE_CONFIG, ...config.lifecycle };
    this.lifecycle = new Lifecycle(lifecycleConfig, {
      onHeartbeat: () => {
        // Re-resolve every beat, not just on state changes: an outstanding
        // background task can finish — or hit its safety cap — while the
        // runner sits idle, and nothing else would flip the status back.
        const loopStatus = this.refreshLoopStatus();
        this.connection.sendHeartbeat(this.loopLag.takeMaxLagMs(), loopStatus);
      },
      onIdleTimeout: () => {
        process.stderr.write("[conveyor-agent] Idle timeout reached, stopping agent\n");
        this.stopped = true;
        // A prefilled initial query parks inside executeQuery (no
        // inputResolver) — abort it so the wait actually ends.
        this.queryBridge?.stop();
        if (this.inputResolver) {
          const resolver = this.inputResolver;
          this.inputResolver = null;
          resolver(null);
        }
      },
      onDormantTimeout: () => {
        process.stderr.write("[conveyor-agent] Dormant idle timeout reached, shutting down\n");
        this.stopped = true;
        this.queryBridge?.stop();
        if (this.inputResolver) {
          const resolver = this.inputResolver;
          this.inputResolver = null;
          resolver(null);
        }
      },
      onTokenRefresh: () => void this.refreshGithubToken(),
      onGitFlush: () => void this.periodicGitFlush(),
      onUsageSample: () => void this.sampleAndReportKeyUsage(),
    });
  }

  get state(): AgentRunnerStatus {
    return this._state;
  }

  get sessionId(): string {
    return this.connection.sessionId;
  }

  get isStopped(): boolean {
    return this.stopped;
  }

  /** Wire the boot supervisor handle post-construction — cli.ts constructs it
   *  via startWorkspaceCommandsAfterConnect() (which needs connect() to already
   *  be callable), so it can't be a constructor dependency. */
  setWorkspaceCommands(supervisor: SessionRunnerWorkspaceCommands | null): void {
    this.workspaceCommands = supervisor;
  }

  // ── Main lifecycle ─────────────────────────────────────────────────

  /**
   * Establish the API connection, wire callbacks, and join the session room.
   * Call this before run() when you need to send events (e.g. setup/start
   * command output) before the main agent lifecycle begins.
   */
  async connect(): Promise<boolean> {
    await this.setState("connecting");

    // 1. Connect
    await this.connection.connect();
    await this.setState("connected");
    this.connection.sendEvent({ type: "connected", sessionId: this.sessionId });

    // 2. Wire callbacks
    this.wireConnectionCallbacks();
    // Off-pod (codespace) boot milestones ride the socket — register the
    // fallback as soon as the authenticated connection exists so the
    // supervisor's start_command_launched (and friends) reach the meter.
    registerBootMilestoneSocketFallback((key) => this.connection.reportBootMilestone(key));
    this.lifecycle.startHeartbeat();
    // Loop-lag measurement + the starvation-proof heartbeat worker: a stalled
    // main loop must not silence liveness (lease TTL 150s + 120s grace) — the
    // worker keeps beating with `loopLagMs` so the API can tell busy from dead.
    this.loopLag.start();
    this.connection.startHeartbeatWorker(this.loopLag.sharedBuffer);
    this.lifecycle.startTokenRefresh();
    // Report the running subscription key's usage right away, then periodically.
    // Read-only `claude -p "/usage"` subprocess — safe to start pre-`run()`,
    // unlike the git-flush timer below.
    this.lifecycle.startUsageSample();
    // NOTE: the periodic git-flush timer is NOT started here. It touches the
    // repo (git add/stash/reset + force-push conveyor-wip/<branch>) with no
    // git-ready guard of its own, so starting it before `run()`'s
    // `awaitGitReady` gate resolves could race entrypoint.sh's backgrounded
    // checkout/merge (index.lock contention, or a force-push over a
    // not-yet-restored WIP snapshot). It is started in `run()` instead, only
    // once git prep is confirmed ready.

    // 2.4. Prefetch the task context CONCURRENTLY with the room join and the
    // port-baseline scan below — it needs only the authenticated socket, and
    // it is the longest server roundtrip on the boot path (history included).
    // run() awaits this promise; the branch-catch suppresses an unhandled
    // rejection if connect() bails (stop/shutdown) before run() consumes it —
    // awaiting the original promise still surfaces the error.
    this.contextPrefetch = this.connection.call("getTaskContext", {
      sessionId: this.sessionId,
      includeHistory: true,
    });
    this.contextPrefetch.catch(() => {});

    // 2.5. Join session room + retrieve pending messages, and establish the
    // preview-port baseline, concurrently — they are independent. The baseline
    // must exist before releasing the caller to launch setup/start commands:
    // anything already listening (sshd 2222, sidecars, agent internals)
    // belongs in the baseline; application ports opened by workspace commands
    // must be observed by a later poll instead. Unsupported hosts remain
    // non-fatal (a host without /proc/net/tcp disables preview discovery),
    // but a stop while the baseline is pending closes this startup gate so
    // callers cannot launch commands.
    const serverMessages = await this.connectAgentSession();
    // A persistent authorization denial (the session's user is not a project
    // member) can never be fixed by a retry or token refresh — every agent RPC
    // fails ACL. Park cleanly instead of letting the rejection crash the
    // process into the supervisor's 3× restart loop. connect() returning false
    // routes the CLI to its graceful stop-before-commands exit (code 0).
    if (serverMessages === null) return false;
    for (const msg of serverMessages) {
      if (msg.content) {
        this.pendingMessages.push({ content: msg.content, userId: msg.userId });
      }
    }
    if (this.stopped) return false;

    // 2.6. Fire-and-forget: tell the API our agent version. If a newer
    // @rallycry/conveyor-agent is on NPM, the API will queue a base image
    // rebuild for the project (deduped per-project across concurrent agents).
    // We continue running with the current version regardless.
    const agentVersion = readAgentVersion();
    if (agentVersion) {
      this.connection
        .call("notifyAgentVersion", { sessionId: this.sessionId, agentVersion })
        .catch(() => {
          // Best-effort — don't block agent startup if NPM lookup or rebuild fails
        });
    }
    return true;
  }

  /**
   * The `connectAgent` handshake (joins the session room, drains pending
   * messages), run concurrently with the preview-port baseline scan. Returns
   * the server's pending messages, or `null` when the session is not authorized
   * for the project — a permission denial no retry or token refresh can fix, so
   * the caller parks cleanly rather than crash-looping. Any other error is
   * rethrown (transient — the normal startup-failure path handles it).
   */
  private async connectAgentSession(): Promise<
    Array<{ content: string; userId: string; createdAt: string }> | null
  > {
    try {
      const [{ pendingMessages }] = await Promise.all([
        this.connection.call("connectAgent", { sessionId: this.sessionId }),
        this.portDiscovery.start().catch(() => {}),
      ]);
      return pendingMessages;
    } catch (err) {
      if (!isPermissionDeniedError(err)) throw err;
      process.stderr.write(
        `[conveyor-agent] Not authorized for this project (session ${this.sessionId}) — ` +
          `the session user lacks project access; parking instead of retrying.\n`,
      );
      return null;
    }
  }

  /**
   * Run the main agent lifecycle: fetch context, resolve mode, execute, loop.
   * Requires connect() to have been called first.
   */
  // oxlint-disable-next-line max-lines-per-function, complexity -- lifecycle orchestration is inherently sequential
  async run(): Promise<void> {
    // 3. Fetch context — normally already in flight since connect() (the
    // prefetch overlaps the room join + port scan); the direct call is the
    // fallback for callers that drive run() without connect() (tests).
    await this.setState("fetching_context");
    try {
      const ctx = await (this.contextPrefetch ??
        this.connection.call("getTaskContext", {
          sessionId: this.sessionId,
          includeHistory: true,
        }));
      this.fullContext = this.buildFullContext(ctx);
      this.taskContext = {
        status: ctx.status,
        plan: ctx.plan,
        storyPointId:
          ctx.storyPoints === null || ctx.storyPoints === undefined
            ? null
            : String(ctx.storyPoints),
        model: ctx.model,
        githubPRUrl: ctx.githubPRUrl,
        isParentTask: this.fullContext.isParentTask,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to fetch task context";
      this.connection.sendEvent({ type: "error", message });
      await this.callbacks.onEvent({ type: "error", message });
      await this.shutdown("error");
      return;
    }

    // 3.5. Initial git setup. In split-mode pods, the workbench daemon runs
    // git prep (fetch + checkout) in the BACKGROUND (so the card lights up
    // before git finishes) and reports its state over the `gitStatus`
    // loopback op. Gate Claude on that state: it must never spawn on a stale
    // branch. `awaitGitReady` returns "not-gated" immediately when the
    // workbench split isn't enabled (GitHub Codespaces / local), preserving
    // those environments' own git-sync below.
    const gitState = await awaitGitReady({
      onLog: (m) => process.stderr.write(`[conveyor-agent] ${m}\n`),
    });
    if (gitState === "failed" || gitState === "timeout") {
      const message =
        gitState === "failed"
          ? "Workspace git preparation failed (see pod logs)"
          : "Workspace git preparation timed out (see pod logs)";
      this.connection.sendEvent({ type: "error", message });
      await this.callbacks.onEvent({ type: "error", message });
      // Do NOT spawn Claude on a broken/absent repo — mirror the context-fetch
      // failure handling above.
      await this.shutdown("error");
      return;
    }

    // 3.5b. Authoritative checkout — the entrypoint only guarantees a repo dir
    // with an origin remote; the agent owns branch selection + wip restore.
    // We do NOT merge origin/<base> here: WIP restores cleanly onto the exact
    // HEAD it was cut from, and the agent merges base when it chooses to.
    if (this.fullContext?.githubBranch) {
      const ok = await ensureOnTaskBranch(
        this.config.workspaceDir,
        this.fullContext.githubBranch,
        this.fullContext.baseBranch ?? undefined,
      );
      if (!ok) {
        process.stderr.write("[conveyor-agent] WARNING: task-branch checkout failed\n");
      }
      if (ok) void reportBootMilestone({ key: "branch_ready" });
    }

    // Git prep is confirmed ready — the workbench daemon's GitPrepJob reported
    // "ready" over the gitStatus op (the state awaitGitReady polls for), and
    // the authoritative checkout above has now finished — so it is finally
    // safe to arm the periodic WIP flush timer. Starting it any earlier (e.g.
    // in connect(), or before the block above) races either the daemon's
    // backgrounded git prep or the checkout above: the flush takes
    // .git/index.lock and force-pushes conveyor-wip/<branch> with no
    // git-ready guard of its own. Skip arming it
    // if the runner is already stopping (e.g. idle/dormant timeout fired while
    // we were gated on git). `startGitFlush()` internally stops any prior timer
    // first, so even if this were reached twice there is no double-start.
    if (!this.stopped) {
      this.lifecycle.startGitFlush();
    }

    // 3.6. Recover uncommitted work a previous pod pushed to conveyor-wip.
    if (this.fullContext?.githubBranch) {
      const restored = await restoreWipSnapshot(
        this.config.workspaceDir,
        this.fullContext.githubBranch,
      );
      if (restored !== "none") {
        process.stderr.write(`[conveyor-agent] WIP snapshot restore: ${restored}\n`);
      }
    }

    // 4. Reconcile mode from server context (overrides env-var defaults)
    this.mode.applyServerMode(this.fullContext?.agentMode, this.fullContext?.isAuto);
    this.mode.resolveInitialMode(this.taskContext);

    // 4.1. Belt-and-braces recovery for auto cards that already HAVE a plan
    // but are stuck in a pre-build status (crash/relaunch that missed the
    // server-side bump): fire the InProgress advance + identification. A
    // plan-less card deliberately stays put — the card advances when its
    // first plan lands (server-side maybeIdentifyOnFirstPlan), never at boot,
    // so a fresh auto card is not identified/moved before any plan exists.
    // Fire-and-forget — best-effort.
    if (
      this.fullContext?.isAuto &&
      PRE_BUILD_TASK_STATUSES.has(this.taskContext.status) &&
      this.mode.isBuildCapable &&
      hasTaskPlan(this.fullContext.plan)
    ) {
      void this.connection.triggerIdentification().catch(() => {});
    }

    // 4.5. Create query bridge for SDK execution
    this.queryBridge = this.createQueryBridge();

    // 5. Log initialization
    this.logInitialization();

    // 6. Execute initial mode
    const staleBatch = [...this.pendingMessages];
    const didExecuteInitialQuery = await this.executeInitialMode();

    // Clear stale pending messages from connectAgent — they were already
    // included in the initial task context (chat history). Any messages
    // that arrived via live socket events DURING initial mode are not in
    // the snapshot, so they survive. Removal is by object identity because
    // executeInitialMode may itself consume/fold entries (prefill paths).
    // Only splice when the initial mode actually ran a query (auto/building/
    // review/pack/discovery/prefill) — a parked `help` prefill skips the query,
    // so its pending messages must be retained for the core loop to process. A
    // stale entry whose content is NOT in the chat-history snapshot (e.g. a
    // queued message that never got a chat row) is kept — silently dropping it
    // would lose the message; the core loop delivers it as a follow-up.
    if (staleBatch.length > 0 && didExecuteInitialQuery) {
      // Non-assistant roles mirror the connect-time chat scan's filter — a
      // pending message always originates from a user/system chat row.
      const historyContents = new Set(
        (this.fullContext?.chatHistory ?? [])
          .filter((m) => m.role !== "assistant" && m.content.trim())
          .map((m) => m.content.trim()),
      );
      for (const stale of staleBatch) {
        if (stale.content.trim() && !historyContents.has(stale.content.trim())) continue;
        const idx = this.pendingMessages.indexOf(stale);
        if (idx !== -1) this.pendingMessages.splice(idx, 1);
      }
    }

    // Discovery ExitPlanMode during the (prefilled) initial query: planning is
    // done and the runner falls through to the normal idle wait below. Clear
    // the flag so a later follow-up's completion isn't mistaken for a fresh
    // discovery completion (which would drain pending messages in coreLoop).
    if (this.queryBridge?.isDiscoveryCompleted) {
      process.stderr.write(
        "[conveyor-agent] Discovery completed during initial query — entering idle\n",
      );
      this.queryBridge.isDiscoveryCompleted = false;
    }

    // 7. Core loop: idle → message → execute → idle
    if (!this.stopped) {
      process.stderr.write(
        `[conveyor-agent] Listening for messages (mode: ${this.mode.effectiveMode})\n`,
      );
    }
    // Fallback release for paths that never ran an initial query (e.g. a
    // non-auto-run mode that fell through to the core loop). The primary
    // release fires much earlier — on the first harness event of the initial
    // query, or when a prefill parks (see createQueryBridge/onEvent and the
    // prefill branches) — so the app's start command is NOT held behind the
    // entire first turn. Idempotent: notifyLoopReady resolves a settled
    // promise as a no-op.
    this.workspaceCommands?.notifyLoopReady();
    while (!this.stopped) {
      if (this._state !== "idle") await this.setState("idle");
      await this.coreLoop();
    }

    // 8. Shutdown
    await this.shutdown("finished");
  }

  /** Convenience wrapper: connect() then run(). */
  async start(): Promise<void> {
    if (!(await this.connect())) return;
    await this.run();
  }

  // ── Core loop ──────────────────────────────────────────────────────

  // oxlint-disable-next-line complexity -- dormant idle paths add branches but are tightly coupled to the loop
  private async coreLoop(): Promise<void> {
    while (!this.stopped) {
      // Defense-in-depth: if the agent already emitted a "completed" event,
      // stop the loop.  The server-side guard should prevent messages from
      // reaching us, but if one leaks through, this prevents the completion
      // cycle (agent says "done" → system message → agent says "done" → …).
      if (this.completedThisTurn) {
        const resumed = await this.handleDormantIdle();
        if (!resumed) break;
        continue;
      }
      if (this._state === "idle") {
        this.lifecycle.startIdleTimer();
        const msg = await this.waitForMessage();
        this.lifecycle.cancelIdleTimer();

        if (!msg) {
          if (this.interrupted && !this.stopped) {
            this.interrupted = false;
            continue;
          }
          break;
        }

        this.interrupted = false;

        if (msg.source === "pty_passive") {
          // Keep-alive PTY: a human typed into the parked, idle Connected-TUI.
          // Drain the passive turn (no prompt fed, no synthetic user_message) so
          // status flips to running and the exchange mirrors into chat.
          await this.setState("running");
          await this.executePassive();
        } else {
          await this.callbacks.onEvent({
            type: "user_message",
            content: msg.content,
            userId: msg.userId,
          });

          // Execute the Claude SDK query with the user's message. A hinted
          // refine message parks in the TUI input for the human instead of
          // auto-submitting.
          if (this.prefillEligible(msg)) {
            await this.runPrefilledMessage(msg, this.mode.effectiveMode);
          } else {
            await this.setState("running");
            await this.executeQuery(msg.content);
          }
        }

        // Discovery ExitPlanMode: agent completed its plan — shut down cleanly.
        // No idle state, no message waiting, no chance of being re-woken.
        if (this.queryBridge?.isDiscoveryCompleted) {
          process.stderr.write(
            "[conveyor-agent] Discovery completed — entering dormant idle (staying connected)\n",
          );
          this.pendingMessages.length = 0;
          if (this._state !== "idle") await this.setState("idle");
          const discoveryMsg = await this.waitForMessage();
          if (!discoveryMsg) break;
          process.stderr.write(
            "[conveyor-agent] Received message while discovery-dormant, resuming\n",
          );
          this.queryBridge.isDiscoveryCompleted = false;
          this.completedThisTurn = false;
          this.pendingMessages.unshift(discoveryMsg);
          continue;
        }

        if (this.stopped) break;
        if (this.interrupted) {
          this.interrupted = false;
          continue;
        }

        await this.flushWipNow("WIP: end of turn");
        if (!this.stopped) await this.setState("idle");
      } else if (this._state === "error") {
        await this.setState("idle");
      } else {
        break;
      }
    }
  }

  /**
   * Handle dormant-after-completed idle. Returns true if a critical message
   * woke us (caller should `continue` the loop), false if we should break
   * (stop signal or dormant timeout fired).
   *
   * The absolute deadline is set on first entry per completion cycle and
   * preserved across re-entries: an inbound critical message cannot extend
   * the bound past `dormantTimeoutMs`. Only a genuine wake (clearing
   * `completedThisTurn`) resets the deadline so the next dormant entry
   * starts a fresh window.
   */
  private async handleDormantIdle(): Promise<boolean> {
    if (this.dormantDeadline === null) {
      this.dormantDeadline = Date.now() + this.lifecycle.config.dormantTimeoutMs;
      process.stderr.write(
        "[conveyor-agent] Completed — entering dormant idle (staying connected)\n",
      );
    }
    await this.flushWipNow("WIP: turn complete");
    this.pendingMessages.length = 0;
    if (this._state !== "idle") await this.setState("idle");
    const remainingMs = Math.max(0, this.dormantDeadline - Date.now());
    this.lifecycle.startDormantTimer(remainingMs);
    const dormantMsg = await this.waitForMessage();
    this.lifecycle.cancelDormantTimer();
    if (!dormantMsg) return false;
    const contentPreview =
      dormantMsg.content.length > 80 ? `${dormantMsg.content.slice(0, 80)}...` : dormantMsg.content;
    process.stderr.write(
      `[conveyor-agent] Received message while dormant, resuming: ` +
        `userId=${dormantMsg.userId}, source=${dormantMsg.source || "unknown"}, ` +
        `content="${contentPreview.replace(/\n/g, "\\n")}"\n`,
    );
    this.completedThisTurn = false;
    this.dormantDeadline = null;
    this.pendingMessages.unshift(dormantMsg);
    return true;
  }

  // ── Initial mode execution ─────────────────────────────────────────

  /** Returns true if an initial query was executed, false otherwise. */
  private async executeInitialMode(): Promise<boolean> {
    if (!this.taskContext || !this.fullContext) return false;
    const effectiveMode = this.mode.effectiveMode;

    const intrinsicDelivery = this.queryBridge?.initialPromptDelivery(this.fullContext) ?? "submit";

    // A single hinted message (Refine → Discovery/Review while the pod was
    // down) parks in the TUI input instead of auto-submitting.
    const hinted = this.prepareInitialPendingMessages(intrinsicDelivery);
    if (hinted) {
      await this.runPrefilledMessage(hinted, effectiveMode);
      return true;
    }

    // Remaining pending chat messages already direct the agent (they arrived
    // before it booted) — force submit semantics so they aren't parked behind
    // an unsubmitted prefill: building/review run the initial query as before,
    // discovery falls through to the core loop which processes them.
    const delivery = this.pendingMessages.length > 0 ? "submit" : intrinsicDelivery;

    // Submit modes auto-run their initial instructions. Interactive discovery
    // now auto-submits too: a fresh interactive card presses Enter on its
    // planner preamble instead of parking an empty TUI. Prefill (PTY task chat,
    // fresh session) still spawns a parked TUI for `help` — the only remaining
    // read-only mode that waits on the human.
    const shouldRun = AUTO_RUN_MODES.has(effectiveMode) || delivery === "prefill";

    if (!shouldRun) {
      await this.setState("idle");
      return false;
    }

    if (delivery === "prefill") {
      // The query parks until the human submits in the TUI. Report
      // waiting_for_input (not running) and bound the wait with the idle
      // timer; the first harness event flips state to running via the
      // onStatusChange hook in createQueryBridge, cancelling the timer.
      await this.setState("waiting_for_input");
      // A parked prefill emits no harness events until the human submits —
      // release the start command now so the app isn't held behind a human.
      this.workspaceCommands?.notifyLoopReady();
      await this.callbacks.onEvent({ type: "execute_mode", mode: effectiveMode, delivery });
      // A chat message may have raced the awaits above — let coreLoop process
      // it instead of parking it behind an unsubmitted prefill.
      if (this.pendingMessages.length > 0) {
        if (!this.stopped) await this.setState("idle");
        return false;
      }
      this.lifecycle.startIdleTimer();
      try {
        await this.executeQuery(undefined, delivery);
      } finally {
        this.lifecycle.cancelIdleTimer();
      }
    } else {
      await this.setState("running");
      await this.callbacks.onEvent({ type: "execute_mode", mode: effectiveMode });
      await this.executeQuery(undefined, delivery);
      await this.requeueWedgedInitialQuery(delivery);
    }
    if (!this.stopped) await this.setState("idle");
    return true;
  }

  /**
   * The mid-turn wedge watchdog aborts a turn that produced no events for 30
   * minutes. Its documented recovery — "the next message respawns with
   * `--resume`" — assumes a human or a follow-up is coming, which is exactly
   * what an autonomous card does NOT have: after the abort the runner goes
   * idle and the card parks until a pack watchdog or a human notices.
   *
   * So for an autonomous card whose INITIAL query was killed as wedged, re-run
   * that initial query once against the (durable, on-disk) resumed session.
   * Bounded to a single retry: a second wedge is a real failure that should
   * surface as a parked card rather than loop the pod. Skipped when a pending
   * message already exists — the core loop delivers that instead, which is the
   * path the watchdog's original recovery assumed.
   */
  private async requeueWedgedInitialQuery(delivery: "submit" | "prefill"): Promise<void> {
    if (!this.queryBridge?.lastTurnWedgeAborted) return;
    if (this.stopped || this.interrupted || this.completedThisTurn) return;
    if (this.pendingMessages.length > 0) return;
    if (!this.mode.isAuto && !AUTONOMOUS_RUNNER_MODES.has(this.config.runnerMode ?? "")) return;
    process.stderr.write(
      "[conveyor-agent] Initial query was aborted as wedged with nothing queued behind it — " +
        "re-running it once (an autonomous card has no follow-up message coming)\n",
    );
    this.connection.sendEvent({
      type: "error",
      message:
        "The first turn stalled and was aborted — retrying it automatically (the conversation resumes from the on-disk session).",
    });
    await this.setState("running");
    await this.executeQuery(undefined, delivery);
  }

  // ── Prefill-delivery helpers ───────────────────────────────────────

  /**
   * Startup-queue preamble: the card's initial message reaches a fresh pod
   * twice — inside the task context (chat history) AND as a queued pending
   * message. When the intrinsic delivery is prefill (manual-mode fresh TUI)
   * or a hinted message is queued (Refine), drop those context-duplicates so
   * they don't force submit semantics — a genuinely-new live message still
   * does. Returns the lone hinted message to park, if any.
   */
  private prepareInitialPendingMessages(
    intrinsicDelivery: "submit" | "prefill",
  ): IncomingMessage | null {
    const hasHintedPending = this.pendingMessages.some((m) => m.delivery === "prefill");
    if (this.pendingMessages.length > 0 && (intrinsicDelivery === "prefill" || hasHintedPending)) {
      this.foldContextDuplicatePendingMessages();
    }
    return this.takePrefillHintedMessage();
  }

  /**
   * Drop pending messages whose content is already present as a user message
   * in the task-context chat history (the card's initial message is delivered
   * both ways). Hinted (prefill) messages are kept — they are consumed by the
   * dedicated prefill path.
   */
  private foldContextDuplicatePendingMessages(): void {
    const userContents = new Set(
      (this.fullContext?.chatHistory ?? [])
        .filter((m) => m.role === "user" && m.content.trim())
        .map((m) => m.content.trim()),
    );
    const kept = this.pendingMessages.filter(
      (m) => m.delivery === "prefill" || !m.content.trim() || !userContents.has(m.content.trim()),
    );
    const dropped = this.pendingMessages.length - kept.length;
    if (dropped > 0) {
      this.pendingMessages.length = 0;
      this.pendingMessages.push(...kept);
      process.stderr.write(
        `[conveyor-agent] Folded ${dropped} pending message(s) already present in chat context\n`,
      );
    }
  }

  /**
   * Honor a prefill hint only when it is unambiguous: PTY harness, a manual
   * (human-facing) agent mode, no other message queued behind it, and not an
   * automated-critical source. Everything else keeps submit semantics.
   */
  private prefillEligible(msg: IncomingMessage): boolean {
    if (msg.delivery !== "prefill") return false;
    if (this.pendingMessages.length > 0) return false;
    if (this.queryBridge?.harnessKind !== "pty") return false;
    // Auto cards must never park a prefill in the TUI — the whole point of auto
    // is to submit and keep working, and a parked prefill blocks the human from
    // typing a follow-up. Submit instead.
    if (this.mode.isAuto) return false;
    // "user" is the queue-flush default source; "mode_change" is the refine
    // wake. Anything else (system/ci_failure/review_trigger/…) must submit.
    if (msg.source && msg.source !== "mode_change" && msg.source !== "user") return false;
    // Manual modes only.
    const m = this.mode.effectiveMode;
    return m === "discovery" || m === "review" || m === "help";
  }

  /** Consume the sole pending message when it should park as a prefill. */
  private takePrefillHintedMessage(): IncomingMessage | null {
    if (this.pendingMessages.length !== 1) return null;
    const [first] = this.pendingMessages;
    if (!first || first.delivery !== "prefill") return null;
    this.pendingMessages.length = 0;
    if (this.prefillEligible(first)) return first;
    this.pendingMessages.push(first);
    return null;
  }

  /**
   * Park a message in the Connected-TUI input (prefill) and wait for the human
   * to submit. Empty content prefills the mode's initial prompt instead (the
   * latest user chat message — e.g. a Refine wake on a dormant agent). The
   * caller restores the idle state afterwards.
   */
  private async runPrefilledMessage(msg: IncomingMessage, effectiveMode: AgentMode): Promise<void> {
    await this.setState("waiting_for_input");
    // Parked on the human — no harness events until submit; release the
    // start command so the app isn't held behind an unsubmitted TUI.
    this.workspaceCommands?.notifyLoopReady();
    await this.callbacks.onEvent({
      type: "execute_mode",
      mode: effectiveMode,
      delivery: "prefill",
    });
    this.lifecycle.startIdleTimer();
    try {
      await this.executeQuery(msg.content.trim() ? msg.content : undefined, "prefill");
    } finally {
      this.lifecycle.cancelIdleTimer();
    }
  }

  // ── Message waiting ────────────────────────────────────────────────

  private waitForMessage(): Promise<IncomingMessage | null> {
    if (this.pendingMessages.length > 0) {
      return Promise.resolve(this.pendingMessages.shift() ?? null);
    }
    return new Promise<IncomingMessage | null>((resolve) => {
      this.inputResolver = resolve;
    });
  }

  /** Inject a message (from connection callback or external source) */
  injectMessage(msg: IncomingMessage): void {
    if (this.inputResolver) {
      const resolve = this.inputResolver;
      this.inputResolver = null;
      resolve(msg);
      return;
    }
    // A plain same-mode user follow-up arriving mid-turn: paste it into the LIVE
    // running TUI (the CLI queues it and picks it up at the next turn boundary)
    // instead of aborting the turn — which killed the running `claude` process
    // and respawned it. Only for genuine user messages on a running PTY turn;
    // mode changes, prefills, passive sentinels, and system/critical sources
    // still supersede via stop() + respawn below.
    if (
      this._state === "running" &&
      this.canInjectIntoRunningTurn(msg) &&
      this.queryBridge?.injectIntoRunningTurn(msg.content)
    ) {
      void this.callbacks.onEvent({
        type: "user_message",
        content: msg.content,
        userId: msg.userId,
      });
      return;
    }
    this.pendingMessages.push(msg);
    // Interrupt running query so the new message is processed quickly.
    // waiting_for_input covers a prefilled-but-unsubmitted TUI: the chat
    // message supersedes the prefill (teardown + respawn as follow-up).
    if (this._state === "running" || this._state === "waiting_for_input") {
      this.queryBridge?.stop();
    }
  }

  /**
   * Whether a mid-turn message may be pasted into the live running TUI rather
   * than superseding the turn. Restricted to genuine same-mode user follow-ups:
   * a pending mode restart, an empty body, a prefill hint, or any non-"user"
   * source (mode_change / pty_passive / system / ci_failure / review_trigger)
   * must take the abort+respawn path — a mode/fingerprint change needs a fresh
   * spawn, and a prefill must park unsubmitted for the human.
   */
  private canInjectIntoRunningTurn(msg: IncomingMessage): boolean {
    if (this.mode.pendingModeRestart) return false;
    if (!msg.content.trim()) return false;
    if (msg.delivery === "prefill") return false;
    if (msg.source && msg.source !== "user") return false;
    return true;
  }

  // ── Query execution with abort handling ────────────────────────────

  /** Run queryBridge.execute, swallowing abort errors from stop/softStop. */
  private async executeQuery(
    followUpContent?: string,
    promptDelivery?: "submit" | "prefill",
  ): Promise<void> {
    if (!this.fullContext || !this.queryBridge) return;
    try {
      await this.queryBridge.execute(this.fullContext, followUpContent, promptDelivery);
    } catch (err) {
      if (this.interrupted || this.stopped) {
        process.stderr.write("[conveyor-agent] Query aborted by stop/softStop signal\n");
        return;
      }
      throw err;
    }
  }

  /**
   * A human typed into the parked, idle Connected-TUI (keep-alive PTY). Wake the
   * core loop with a sentinel so it drains a passive turn. No-op once stopped.
   */
  private onPassiveTuiActivity(): void {
    if (this.stopped) return;
    this.injectMessage({ content: "", userId: "human", source: "pty_passive" });
  }

  /** Run queryBridge.executePassive, swallowing abort errors from stop/softStop. */
  private async executePassive(): Promise<void> {
    if (!this.fullContext || !this.queryBridge) return;
    try {
      await this.queryBridge.executePassive(this.fullContext);
    } catch (err) {
      if (this.interrupted || this.stopped) {
        process.stderr.write("[conveyor-agent] Passive turn aborted by stop/softStop signal\n");
        return;
      }
      throw err;
    }
  }

  // ── Stop / soft-stop ───────────────────────────────────────────────

  /** Shared token-refresh closure for the git-flush call sites (periodic
   *  backstop + per-turn flush). Never throws — returns undefined so the
   *  caller falls back to its existing remote credentials. */
  private async refreshGithubTokenForFlush(): Promise<string | undefined> {
    try {
      const res = await this.connection.call("refreshGithubToken", {
        sessionId: this.connection.sessionId,
      });
      return res.token;
    } catch {
      return undefined;
    }
  }

  /** Push uncommitted work to the conveyor-wip ref after a turn. Git-only (no
   *  GCS). Primary-branch only — cheap enough to run every turn. Shares the
   *  in-flight guard with the periodic backstop so a turn-end flush and a
   *  timer tick never overlap. Best-effort, never throws. */
  private async flushWipNow(wipMessage: string): Promise<void> {
    if (this.periodicFlushInFlight || this.stopped) return;
    this.periodicFlushInFlight = true;
    try {
      await flushPendingChanges(this.config.workspaceDir, {
        wipMessage,
        refreshToken: () => this.refreshGithubTokenForFlush(),
      });
    } catch {
      // best effort — the next turn or the backstop timer retries
    } finally {
      this.periodicFlushInFlight = false;
    }
  }

  /** Periodic best-effort WIP commit + push during normal agent execution.
   *  Covers ungraceful pod termination (OOMKilled, node crash/eviction) where
   *  the preStop hook + SIGTERM flush don't get a chance to run. No-ops on a
   *  clean tree. Guarded so two ticks can't overlap. Never throws. */
  private async periodicGitFlush(): Promise<void> {
    if (this.periodicFlushInFlight || this.stopped) return;
    // A running heavy gate (test/typecheck/build via singleton.sh) owns the
    // disk and churns the tree — flushing mid-gate snapshots build garbage
    // while competing for the exact IO the gate is starving on. Defer to the
    // next tick, but never indefinitely: a wedged gate must not disable crash
    // protection, so after MAX_GATE_SKIPS consecutive skips we flush anyway.
    if (isHeavyGateActive() && this.gateSkipCount < SessionRunner.MAX_GATE_SKIPS) {
      this.gateSkipCount++;
      if (this.gateSkipCount === 1) {
        process.stderr.write("[conveyor-agent] Periodic git flush deferred — heavy gate running\n");
      }
      return;
    }
    this.gateSkipCount = 0;
    this.periodicFlushInFlight = true;
    try {
      const result = await flushAllPendingWork(this.config.workspaceDir, {
        wipMessage: "WIP: periodic auto-commit",
        refreshToken: () => this.refreshGithubTokenForFlush(),
      });
      if (result.hadWork) {
        process.stderr.write(
          `[conveyor-agent] Periodic git flush: branchesBackedUp=${result.branchesBackedUp} worktreesSnapshotted=${result.worktreesSnapshotted}\n`,
        );
      }
    } catch {
      // best effort — next tick will retry
    } finally {
      this.periodicFlushInFlight = false;
    }
  }

  /** Sample the running Claude subscription key's rate-limit utilization from
   *  the Claude CLI `/usage` command and report it as `rate_limit_update` events.
   *  The API (`persistRateLimitSnapshot`) attributes them to the key this session
   *  launched under, keeping User Settings + the PtY-tab usage widget fresh and
   *  `selectBestKey` rotation honest. Best-effort — never throws, no-op when the
   *  pod has no OAuth token (e.g. API-key projects). */
  private async sampleAndReportKeyUsage(): Promise<void> {
    if (this.stopped) return;
    const samples = await sampleKeyUsage(process.env.CLAUDE_CODE_OAUTH_TOKEN);
    for (const sample of samples) {
      this.connection.sendEvent({
        type: "rate_limit_update",
        rateLimitType: sample.rateLimitType,
        utilization: sample.utilization,
        status: sample.status,
        resetsAt: sample.resetsAt ?? undefined,
        gauges: sample.gauges,
      });
    }
  }

  /** Wait (bounded) for any in-flight periodic/turn flush to release the shared
   *  guard, so a shutdown flush never races it on `.git/index.lock`. */
  private async waitForFlushSlot(maxWaitMs = 5000): Promise<void> {
    const start = Date.now();
    while (this.periodicFlushInFlight && Date.now() - start < maxWaitMs) {
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 50);
      });
    }
  }

  /** Best-effort WIP commit + push on shutdown so in-flight work isn't lost
   *  when a claudespace pod is killed. Must be called BEFORE stop() so the
   *  connection is still alive for token refresh. Shares the in-flight guard
   *  with the periodic/turn flushes (waits one out, then claims the slot) so a
   *  SIGTERM mid-flush can't run two concurrent flushes racing the index lock.
   *  Never throws. */
  async flushGitOnShutdown(): Promise<void> {
    await this.waitForFlushSlot();
    if (this.periodicFlushInFlight) {
      // Still busy after the wait — skip rather than race the lock. Whatever is
      // running already captured recent state.
      process.stderr.write("[conveyor-agent] Shutdown git flush skipped — flush still in flight\n");
      return;
    }
    this.periodicFlushInFlight = true;
    try {
      const result = await flushAllPendingWork(this.config.workspaceDir, {
        wipMessage: "WIP: auto-commit on conveyor-agent shutdown",
        refreshToken: async () => {
          try {
            const res = await this.connection.call("refreshGithubToken", {
              sessionId: this.connection.sessionId,
            });
            return res.token;
          } catch {
            return undefined;
          }
        },
      });
      if (result.hadWork) {
        process.stderr.write(
          `[conveyor-agent] Shutdown git flush: branchesBackedUp=${result.branchesBackedUp} worktreesSnapshotted=${result.worktreesSnapshotted}\n`,
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      process.stderr.write(`[conveyor-agent] Shutdown git flush failed: ${msg}\n`);
    } finally {
      this.periodicFlushInFlight = false;
    }
  }

  /** Server-pushed `session:stop`: flush WIP BEFORE tearing down. Plain `stop()`
   *  sets `stopped=true` and disconnects with no flush, losing everything since
   *  the last periodic flush (≤2 min, up to ~20 min behind a gate-skipping
   *  backstop). Flush while the connection is still alive (token refresh), then
   *  stop. Idempotent + best-effort; `stop()` always runs. */
  private async stopWithFlush(): Promise<void> {
    if (this.stopped) return;
    // Halt the running turn immediately (the server asked us to stop) but keep
    // the connection alive so the flush can refresh the push token.
    this.queryBridge?.stop();
    try {
      await this.flushGitOnShutdown();
    } finally {
      this.stop();
    }
  }

  stop(): void {
    this.stopped = true;
    this.queryBridge?.stop();
    // Keep-alive PTY: kill any parked/active CLI process (fire-and-forget — the
    // server also clears the ring on terminal session status, so a lost ended
    // signal here is covered).
    void this.queryBridge?.dispose().catch(() => {});
    this.portDiscovery.stop();
    // Nothing this session backgrounded outlives the pod's agent process —
    // drop the entries so a stale one can't skew the final status.
    this.backgroundWork.clear();
    this.loopLag.stop();
    this.lifecycle.destroy();
    this.connection.disconnect();
    if (this.inputResolver) {
      const resolver = this.inputResolver;
      this.inputResolver = null;
      resolver(null);
    }
  }

  softStop(): void {
    this.interrupted = true;
    this.queryBridge?.stop();
    // Only null-resolve if no flushed messages are already pending — a message
    // arriving via injectMessage() before the soft stop signal means the agent
    // should process it instead of discarding with a null resolution.
    if (this.inputResolver && this.pendingMessages.length === 0) {
      const resolver = this.inputResolver;
      this.inputResolver = null;
      resolver(null);
    }
  }

  // ── Context & bridge construction ────────────────────────────────

  // oxlint-disable-next-line complexity
  private buildFullContext(ctx: TaskContextDTO): TaskContext {
    const chatHistory = mapChatHistory(ctx.chatHistory);

    return {
      taskId: ctx.id,
      projectId: ctx.projectId ?? "",
      title: ctx.title,
      description: ctx.description,
      plan: ctx.plan,
      status: ctx.status,
      chatHistory,
      agentId: ctx.agentId ?? null,
      _runnerSessionId: this.sessionId,
      agentInstructions: ctx.agentInstructions ?? "",
      model: ctx.model,
      githubBranch: ctx.githubBranch ?? "",
      baseBranch: ctx.baseBranch ?? "",
      projectName: ctx.projectName ?? null,
      projectDescription: ctx.projectDescription ?? null,
      githubPRUrl: ctx.githubPRUrl,
      claudeSessionId: ctx.claudeSessionId ?? null,
      lastSeenMessageId: ctx.lastSeenMessageId ?? null,
      isParentTask: ctx.isParentTask ?? false,
      storyPoints: ctx.storyPoints ?? undefined,
      projectAgents: ctx.projectAgents ?? undefined,
      projectTags: ctx.projectTags ?? undefined,
      taskTagIds: ctx.taskTagIds ?? undefined,
      projectObjectives: ctx.projectObjectives ?? undefined,
      incidents: ctx.incidents ?? undefined,
      recentRelatedTasks: ctx.recentRelatedTasks ?? undefined,
      agentSettings: ctx.agentSettings ?? null,
      agentMode: ctx.agentMode ?? undefined,
      isAuto: ctx.isAuto,
    };
  }

  private createQueryBridge(): QueryBridge {
    const runnerConfig: AgentRunnerConfig = {
      conveyorApiUrl: this.config.connection.apiUrl,
      taskToken: this.config.connection.taskToken,
      taskId: this.fullContext?.taskId ?? "",
      model: this.fullContext?.model ?? DEFAULT_SONNET_MODEL,
      instructions: this.fullContext?.agentInstructions ?? "",
      workspaceDir: this.config.workspaceDir,
      mode: this.config.runnerMode,
      isAuto: this.config.isAuto,
    };

    const bridge = new QueryBridge(this.connection, this.mode, runnerConfig, {
      onStatusChange: (status) => {
        // A prefilled initial query reports running when the human submits
        // in the TUI (first harness event) — leave waiting_for_input and
        // stop the prefill-wait idle timer so a long working turn isn't
        // killed by it.
        if (status === "running" && this._state === "waiting_for_input") {
          this._state = "running";
          this.lifecycle.cancelIdleTimer();
        }
        return this.callbacks.onStatusChange(status as AgentRunnerStatus);
      },
      onEvent: (event) => {
        // A harness event means the PTY is spawned and emitting — release the
        // app's start command now instead of holding it through the whole
        // first turn (idempotent; see workspace-command-supervisor.ts), and
        // report the "agent working" boot milestone once per process.
        this.workspaceCommands?.notifyLoopReady();
        if (!this.agentLiveReported) {
          this.agentLiveReported = true;
          void reportBootMilestone({ key: "agent_live" });
        }
        // A backgrounded gate/subagent launched in this turn outlives it —
        // remember it so the end-of-turn heartbeat doesn't report idle and let
        // the pod's activity clock expire while it runs.
        this.noteToolUseForBackgroundWork(event as Record<string, unknown>);
        // Track completion for the agent-side guard — once the agent emits
        // "completed", the core loop will stop accepting further messages.
        if ((event as Record<string, unknown>).type === "completed") {
          this.completedThisTurn = true;
          // Immediately refresh the server's heartbeat timestamp so
          // lastHeartbeatAt can't drift stale while the agent sits dormant
          // waiting for a critical wake-up (e.g. review_trigger). The
          // periodic 30s timer would otherwise leave up to a ~29s gap
          // between the `completed` event and the next heartbeat.
          void this.connection.sendHeartbeat();
        }
        return this.callbacks.onEvent(event);
      },
    });

    bridge.isParentTask = this.fullContext?.isParentTask ?? false;
    // The CLI's `<task-notification>` turn — one backgrounded gate/subagent
    // finished, so one outstanding entry retires (PTY harness only; on the SDK
    // harness entries expire on the tracker's safety cap instead).
    bridge.onBackgroundTaskDone = () => this.backgroundWork.noteCompletion();

    bridge.onSoftStop = () => {
      process.stderr.write("[conveyor-agent] Soft stop requested (discovery ExitPlanMode)\n");
      this.softStop();
    };

    bridge.onModeTransition = (newMode: AgentMode) => {
      const oldMode = this.mode.effectiveMode;
      process.stderr.write(`[conveyor-agent] Mode transition: ${oldMode} → ${newMode}\n`);
      this.connection.sendEvent({ type: "mode_transition", from: oldMode, to: newMode });
      this.mode.pendingModeRestart = true;
      this.connection.emitModeChanged(newMode);
      this.softStop();
    };

    // Keep-alive PTY: a human typing into the parked, idle Connected-TUI wakes a
    // passive turn (no-op on the SDK harness).
    bridge.onPassiveActivity(() => this.onPassiveTuiActivity());

    return bridge;
  }

  // ── Private helpers ────────────────────────────────────────────────

  private wireConnectionCallbacks(): void {
    this.connection.onMessage((msg) => this.injectMessage(msg));
    this.connection.onStop(() => void this.stopWithFlush());
    this.connection.onSoftStop(() => this.softStop());
    // After a reconnect the API process may be a fresh instance (deploy,
    // restart) whose PTY scrollback ring is empty — force a repaint so the
    // Connected-TUI terminal re-seeds instead of staying blank/hidden until
    // the CLI happens to draw again.
    this.connection.onReconnected = () => this.queryBridge?.forceRepaint();
    this.connection.onModeChange((data) => {
      const action = this.mode.handleModeChange(data.agentMode, this.taskContext);
      if (action.type === "start_auto") {
        this.connection.emitModeChanged(this.mode.effectiveMode);
        this.softStop();
      } else if (action.type === "restart_query") {
        // Server-initiated mode transition (e.g., CI-triggered review).
        // Clear claudeSessionId to prevent SDK resume across model changes
        // (reviewer agent may use a different model than the task agent).
        if (this.fullContext && action.newMode === "review") {
          this.fullContext.claudeSessionId = null;
        }
        // On building transition, re-fetch context to pick up the newly created
        // branch (branch creation is deferred until building mode).
        // Also refresh for "auto" post-exit (defense-in-depth if "auto" leaks through).
        if (
          this.fullContext &&
          (action.newMode === "building" ||
            (action.newMode === "auto" && this.mode.hasExitedPlanMode))
        ) {
          void this.refreshBranchForBuilding();
        }
        // Exit dormant idle so the review_trigger message that follows
        // is processed in the normal idle path (which handles interrupted
        // correctly without draining pendingMessages).
        this.completedThisTurn = false;
        this.connection.emitModeChanged(action.newMode);
        this.softStop();
      }
    });
    this.connection.onApiKeyUpdate((data) => {
      if (data.isSubscription) {
        process.env.CLAUDE_CODE_OAUTH_TOKEN = data.apiKey;
        delete process.env.ANTHROPIC_API_KEY;
        // The interactive TUI reads credentials from disk, not the env var —
        // refresh the synthesized file so the next PTY spawn (and any on-401
        // re-read by a live CLI) picks up the rotated subscription token.
        void ensureClaudeCredentials();
      } else {
        process.env.ANTHROPIC_API_KEY = data.apiKey;
        delete process.env.CLAUDE_CODE_OAUTH_TOKEN;
        // Drop our synthesized subscription credentials so a stale token
        // can't keep authenticating future TUI spawns over the API key.
        void removeConveyorCredentials();
      }
    });
    this.connection.onPullBranch(({ branch }) => {
      void handlePullBranch(this.config.workspaceDir, branch);
    });
  }

  /** Proactively refresh the GitHub token before the 1-hour expiry. */
  private async refreshGithubToken(): Promise<void> {
    try {
      const res = await this.connection.call("refreshGithubToken", {
        sessionId: this.connection.sessionId,
      });
      await updateRemoteToken(this.config.workspaceDir, res.token);
      process.env.GITHUB_TOKEN = res.token;
      process.env.GH_TOKEN = res.token;
      process.stderr.write("[conveyor-agent] Proactively refreshed GitHub token\n");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      process.stderr.write(`[conveyor-agent] Warning: proactive token refresh failed: ${msg}\n`);
    }
  }

  /** Re-fetch task context to pick up a newly created branch and check it out. */
  private async refreshBranchForBuilding(): Promise<void> {
    try {
      const ctx = await this.connection.call("getTaskContext", {
        sessionId: this.sessionId,
        includeHistory: false,
      });
      if (ctx?.githubBranch && this.fullContext) {
        this.fullContext.githubBranch = ctx.githubBranch;
        await ensureOnTaskBranch(this.config.workspaceDir, ctx.githubBranch);
      }
    } catch {
      process.stderr.write(
        "[conveyor-agent] Warning: failed to refresh branch for building transition\n",
      );
    }
  }

  /**
   * The loop status the heartbeats should carry right now.
   *
   * `idle` requires BOTH a quiet runner and a quiet pod: an idle runner with a
   * `run_in_background` gate still running reports `waiting`, which reaches the
   * API as `active` and therefore bumps `Workspace.activityExpiresAt` (an idle
   * heartbeat does not — `heartbeatBumps` is `status !== "idle"`). Without this
   * the reconciler sleeps the pod one activity window after the turn ends and
   * kills the gate. Note the runner's own `AgentRunnerStatus` stays `idle`:
   * this is a liveness signal, not a UI one, so cards still read as idle.
   *
   * "Quiet runner" is `loopStatusForRunnerStatus`, NOT `_state === "idle"`.
   * The old `_state !== "idle" → active` test made every parked state report
   * work — above all `waiting_for_input`, a prefilled TUI sitting on a human,
   * which renewed the clock on every beat and kept the card "active" on the
   * board for the runner's whole 30-minute idle timer regardless of the
   * project's inactivity window. See connection/loop-lag.ts for the table and
   * for why an unknown status still fails open to `active`.
   */
  private resolveLoopStatus(): LoopStatus {
    if (loopStatusForRunnerStatus(this._state) === "active") return "active";
    return this.backgroundWork.hasPending() ? "waiting" : "idle";
  }

  /** Mirror the resolved status into the loop-lag buffer so the starvation-proof
   *  heartbeat worker reports it too. Returns what it wrote. */
  private refreshLoopStatus(): LoopStatus {
    const loopStatus = this.resolveLoopStatus();
    this.loopLag.setStatus(loopStatus);
    return loopStatus;
  }

  /** One `tool_use` from the turn stream — registers a background launch when
   *  the tool starts work that outlives the turn. */
  private noteToolUseForBackgroundWork(event: Record<string, unknown>): void {
    if (event.type !== "tool_use" || typeof event.tool !== "string") return;
    this.backgroundWork.noteToolUse(event.tool, event.input);
  }

  private async setState(status: AgentRunnerStatus): Promise<void> {
    this._state = status;
    this.refreshLoopStatus();
    await this.connection.emitStatus(status);
    await this.callbacks.onStatusChange(status);
  }

  private async shutdown(finalState: AgentRunnerStatus): Promise<void> {
    process.stderr.write(`[conveyor-agent] Shutdown: reason=${finalState}\n`);
    this.connection.sendEvent({ type: "shutdown", reason: finalState });
    this.portDiscovery.stop();
    // Nothing this session backgrounded outlives the pod's agent process —
    // drop the entries so a stale one can't skew the final status.
    this.backgroundWork.clear();
    this.loopLag.stop();
    this.lifecycle.destroy();
    // Keep-alive PTY: tear down the parked/active CLI process before we drop the
    // socket, so the ended signal (ring teardown → tab hides) can go out.
    try {
      await this.queryBridge?.dispose();
    } catch {
      /* best effort */
    }
    try {
      await this.setState(finalState);
    } catch {
      // Best effort — when stop() already tore down the connection the status
      // RPC rejects. A stopped agent should still finish run() cleanly so the
      // process exits 0 instead of taking the error path.
    }
    this.connection.disconnect();
    this._finalState = finalState;
  }

  private _finalState: AgentRunnerStatus | null = null;

  /** The final status after run() completes. Use to determine exit code. */
  get finalState(): AgentRunnerStatus | null {
    return this._finalState;
  }

  private buildTaskContextSnapshot(): Record<string, unknown> {
    return {
      isParentTask: this.fullContext?.isParentTask ?? false,
      status: this.taskContext?.status,
      taskTitle: this.fullContext?.title,
      hasExistingPR: !!this.fullContext?.githubPRUrl,
      hasExistingSession: !!this.fullContext?.claudeSessionId,
      chatHistoryLength: this.fullContext?.chatHistory?.length ?? 0,
      tagIds: this.fullContext?.taskTagIds ?? [],
    };
  }

  private buildInitializationContext(): Record<string, unknown> {
    return {
      mode: this.mode.effectiveMode,
      runnerMode: this.config.runnerMode ?? "task",
      sessionId: this.sessionId,
      ...this.buildTaskContextSnapshot(),
      model: this.taskContext?.model,
      isAuto: this.config.isAuto ?? false,
      subscriptionKeyLabel: process.env.CONVEYOR_SUBSCRIPTION_KEY_LABEL ?? null,
    };
  }

  private logInitialization(): void {
    const context = this.buildInitializationContext();
    process.stderr.write(`[conveyor-agent] Initialized: ${JSON.stringify(context)}\n`);
    this.connection.sendEvent({ type: "session_manifest", ...context });
  }
}
